import { useContext, useEffect, useState, createElement as rc, useCallback } from 'react';
import { setupRulesEngine } from 'lib_ui-core';
import lodash from 'lodash';
const { startCase } = lodash;
import AllTenants from './AllTenants';

import {
    Pressable,
    nativeOnlyStyles,
    fromTheme,
    Caret,
    Card,
    h1,
    View,
    Button,
    styled,
    webOnlyStyles,
    ScrollView,
    hooks,
    WaitScreen,
    contexts
} from 'lib_ui-primitives';
import useEventSink from '../../hooks/useEventSink';
import { constants } from 'lib_ui-services';
const { SessionContext } = contexts;
const { useCaseIds, WELL_KNOWN_ROLE_IDS } = constants;
const { useRouter, useFeatureFlags } = hooks;


let EnvTitle = styled(Pressable).attrs({ name: 'tenant-title' })`
    display: flex;
    flex-direction: row;
    align-items: center;
`;
EnvTitle = nativeOnlyStyles(EnvTitle)`
    margin-top: ${fromTheme('viewMargin')};
    margin-bottom: ${fromTheme('viewMargin')};
`;

EnvTitle.displayName = 'TenantTitle';
let Centered = styled(View).attrs({ name: 'centered' })`
    width: 100%;
    height: 100%;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px;
`;

Centered = webOnlyStyles(Centered)`
    box-sizing: border-box;
`;

const Main = styled(ScrollView).attrs({
    name: 'main',
    contentContainerStyle: { justifyContent: 'center', flexGrow: 1 }
})``;

let FullScreenBody = styled(Card.Body)`
    ${({ theme }) =>
        !theme.mobile &&
        !theme.tablet &&
        `
    margin-left: auto;
    margin-right: auto;
    min-width: 800px;
    `};
`;

FullScreenBody = webOnlyStyles(FullScreenBody)`
    overflow-y: auto;
    ${({ theme }) => theme.mobile && 'padding-inline-start: 0;'};
`;

const Header = styled(h1)`
    font-size: 24px;
`;

const ToggleCaret = styled(Caret).attrs({ name: 'toggle-caret' })`
    margin-right: 6px;
    height: 8px;
    width: 8px;
`;
ToggleCaret.displayName = 'ToggleCaret';

const EMPTY_ARRAY = [];
const EMPTY_SESSION = {};
const _p = { useRouter, hasSupportRoleInOneTouch, useFeatureFlags };
export const _private = _p;

const TenantSelection = () => {
    /** @type {import('../../../../types').StateHook<import('../../../../types').UseCase>} */
    const [selectedUseCase, setSelectedUseCase] = useState();
    const [selectedTenant, setSelectedTenant] = useState();
    const [submitted, setSubmitted] = useState(false);
    const eventSink = useEventSink();
    const [subscribe, publish] = eventSink;
    const session = useContext(SessionContext) || EMPTY_SESSION;
    const { profile } = session;

    useEffect(() => {
        setupRulesEngine(session);
    }, [session]);

    // This avoids a server side lookup of the use cases.
    useEffect(() => {
        return subscribe(
            { verb: 'get', namespace: 'application', relation: 'predefinedRecords' },
            (payload, context) => {
                const useCases = Object.entries(useCaseIds).map(([key, value]) => ({
                    _id: value,
                    title: startCase(key.toLocaleLowerCase())
                }));
                publish({ records: useCases }, { ...context, status: 'success' });
            }
        );
    }, [subscribe, publish]);

    /**
     * @param {React.MouseEvent<HTMLButtonElement>} event
     */

    const onCancel = useCallback(() => {
        publish({ profile: session }, { verb: 'remove', namespace: 'security', relation: 'profile' });
    }, [session, publish]);

    /**
     * @params {React.MouseEvent} event
     * @param {import('../../../../types').UseCase} [useCaseFromDoubleClick]
     */
    const onSubmit = useCallback(
        (event, useCaseFromDoubleClick, tenantFromDoubleClick) => {
            const useCase = useCaseFromDoubleClick ?? selectedUseCase;
            const tenant = tenantFromDoubleClick ?? selectedTenant;
            if (!useCase || !tenant) return;

            setSubmitted(true);

            publish(
                {
                    tenant,
                    useCase,
                    type: 'selectTenant'
                },
                { verb: 'update', namespace: 'security', relation: 'profile', type: 'selectTenant' }
            );
        },
        [publish, selectedUseCase, selectedTenant]
    );

    // prettier-ignore
    return rc(WaitScreen, { waiting: submitted, showSpinner: true },
        !submitted && rc(Centered, null,
            rc(Main, { 'data-testid': 'tenantSelect-content' },
                rc(Card, null,
                    rc(Card.Header, { style: { justifyContent: 'center' } },
                        rc(Header, { name: 'card-header-title', style: { margin: 0 } }, 'Select an Application')
                    ),
                    rc(FullScreenBody, null,
                        rc(AllTenants, {
                            tenants: (profile?.tenant ?? EMPTY_ARRAY),
                            onSubmit,
                            selectedUseCase,
                            setSelectedUseCase,
                            selectedTenant,
                            setSelectedTenant
                        })
                    ),
                    rc(Card.Footer, { style: { justifyContent: 'flex-end' } },
                        rc(Button, { onClick: onCancel, value: 'CANCEL', buttonStyle: 'primary', disabled: submitted }),
                        rc(Button, { onClick: onSubmit, value: 'OK', disabled: !selectedUseCase || submitted, buttonStyle: 'success' })
                    )
                )
            )
        )
    );
};

export default TenantSelection;

// TODO: Create unit tests for this as part of havens feature
function hasSupportRoleInOneTouch(session) {
    return (
        session?.profile?.tenant?.some(t =>
            t.useCase.some(
                uc =>
                    uc['metaui:useCase']._id === useCaseIds.ONE_TOUCH &&
                    uc['identity:role']._id === WELL_KNOWN_ROLE_IDS.SUPPORT
            )
        ) ?? false
    );
}
